import { avatarSrc } from "@/lib/defaults";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { guardOn } from "@/lib/guards";
import { isOffline } from "@/lib/netStatus";
import { readCachedNotifications, writeCachedNotifications } from "@/lib/offlineCache";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { resolveMediaUrl, onMediaImgError } from "@/components/FileUpload";
import { Bell, UserPlus, Heart, MessageCircle, Megaphone, CheckCheck, Radio, MoreHorizontal, Trash2, ChevronDown, Lock, Smartphone, AtSign } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { timeAgo } from "@/lib/timeAgo";
import { groupNotifications, groupTitle } from "@/lib/notifGroup";
import { ModerationStatusModal } from "@/components/ModerationStatusModal";
import { modNotifText } from "@/lib/moderationTexts";
import { FollowRequestNotifActions } from "@/components/privacy/ProfilePrivacy";

const iconFor = (type) => ({ follow: UserPlus, reaction: Heart, comment: MessageCircle, admin: Megaphone, live: Radio, security: Lock, app_campaign: Smartphone, mention: AtSign }[type] || Bell);

export default function Notifications() {
  const { t, config, lang, user, reconnecting } = useApp();
  const brandLogo = config?.settings?.brand?.logo_url ? resolveMediaUrl(config.settings.brand.logo_url) : "/yalola-logo.png";
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [tab, setTab] = useState("all");
  const [confirmClear, setConfirmClear] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (id) => setExpandedGroups((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const deleteGroup = async (row) => {
    const ids = row.items.map((x) => x.id);
    const unreadCount = row.items.filter((x) => !x.read).length;
    setItems((cur) => cur.filter((x) => !ids.includes(x.id)));
    if (unreadCount) setUnread((u) => Math.max(0, u - unreadCount));
    try { await Promise.all(ids.map((id) => api.delete(`/notifications/${id}`))); } catch { toast.error(t("common.error", "Erreur")); }
  };

  // Lot 713 — mode dégradé : dernières notifications gardées sur l'appareil (garde-fou « net_resilience »).
  // Lot 717 — démarrage instantané (garde-fou « instant_boot ») : affichées aussi à l'ouverture en ligne, remplacées en silence.
  const showCached = useCallback(() => {
    if (!user?.id || !guardOn("net_resilience")) return false;
    const c = readCachedNotifications(user.id, tab);
    if (!c) return false;
    setItems((cur) => (cur.length ? cur : c.items));
    setUnread((u) => u || c.unread || 0);
    return true;
  }, [user?.id, tab]);
  const load = useCallback(() => {
    if (reconnecting || isOffline() || guardOn("instant_boot")) showCached();
    api.get(`/notifications?tab=${tab}&limit=100`)
      .then(({ data }) => {
        setItems(data.notifications || []); setUnread(data.unread || 0);
        if (user?.id && guardOn("net_resilience")) writeCachedNotifications(user.id, data.notifications || [], data.unread || 0, tab);
      })
      .catch(() => { showCached(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, lang]);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      if (tab === "unread") setItems([]);
      else setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  const [modModal, setModModal] = useState(null);
  const click = (n) => {
    api.post(`/notifications/${n.id}/read`).catch(() => {});
    // Moderation notifications open the "content under review" modal (live status,
    // clear reason, appeal) instead of a page reload.
    const mod = n.meta?.moderation || n.meta?.appeal;
    if (n.type === "moderation" && mod?.target_type && mod?.target_id) { setModModal(mod); return; }
    if (n.link) nav(n.link);
    else load();
  };

  const deleteOne = async (n) => {
    try {
      await api.delete(`/notifications/${n.id}`);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
      if (!n.read) setUnread((u) => Math.max(0, u - 1));
    } catch { toast.error(t("common.error", "Erreur")); }
  };

  // Profil privé : « X souhaite s'abonner à toi » → Accepter / Refuser directement depuis la notification.
  const patchNotifMeta = (n, patch) => {
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true, meta: { ...x.meta, ...patch } } : x)));
    if (!n.read) setUnread((u) => Math.max(0, u - 1));
  };

  const clearAll = async () => {
    setConfirmClear(false);
    if (items.length === 0) return;
    try {
      await api.delete(`/notifications?tab=${tab}`);
      setItems([]);
      if (tab !== "read") setUnread(0);
      toast.success(t("notif.cleared", "Notifications vidées"));
    } catch { toast.error(t("common.error", "Erreur")); }
  };

  const clearConfirmText = tab === "unread"
    ? t("notif.clear_confirm_unread", "Vider les notifications non lues ?")
    : tab === "read"
      ? t("notif.clear_confirm_read", "Vider les notifications lues ?")
      : t("notif.clear_confirm_all", "Vider toutes les notifications ?");

  const renderItem = (n) => (
    <div
      key={n.id}
      role="button"
      tabIndex={0}
      onClick={() => click(n)}
      onKeyDown={(e) => { if (e.key === "Enter") click(n); }}
      className="w-full flex items-start gap-3 px-5 py-4 text-left transition-colors hover:brightness-[0.98] cursor-pointer"
      style={{ background: n.read ? "transparent" : "var(--yalola-primary-soft)" }}
      data-testid={`notif-page-item-${n.id}`}
    >
      {n.actor ? (
        <Avatar className="h-11 w-11 shrink-0"><AvatarImage src={avatarSrc(n.actor)} /><AvatarFallback>{n.actor.name?.[0]}</AvatarFallback></Avatar>
      ) : (
        <div className="h-11 w-11 shrink-0 rounded-full overflow-hidden grid place-items-center" style={{ background: "var(--yalola-primary)" }}>
          <img src={brandLogo} alt="Yalola" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm leading-snug font-semibold">{modNotifText(n, t)?.title || n.title}</div>
        {(modNotifText(n, t)?.body || n.body) && <div className="text-sm mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{modNotifText(n, t)?.body || n.body}</div>}
        {/* Appeal is handled in the ModerationStatusModal (opened on click) — no inline duplicate CTA. */}
        <FollowRequestNotifActions n={n} onDone={(patch) => patchNotifMeta(n, patch)} />
        <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>{timeAgo(n.created_at, t)}</div>
      </div>
      {!n.read && <span className="mt-2 h-2.5 w-2.5 rounded-full shrink-0" style={{ background: "var(--yalola-primary)" }} />}
      {n.image && <img src={resolveMediaUrl(n.image)} onError={onMediaImgError} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 p-1.5 rounded-full opacity-50 hover:opacity-100 hover:bg-black/5 transition"
            title={t("notif.delete", "Supprimer")}
            data-testid={`notif-page-menu-${n.id}`}
          >
            <MoreHorizontal size={18} style={{ color: "var(--yalola-text-muted)" }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            onClick={(e) => { e.stopPropagation(); deleteOne(n); }}
            className="text-red-600 focus:text-red-600 cursor-pointer"
            data-testid={`notif-page-delete-${n.id}`}
          >
            <Trash2 size={15} className="mr-2" /> {t("notif.delete", "Supprimer")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto" data-testid="notifications-page">
        <div className="yalola-card p-0 overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b" style={{ borderColor: "var(--yalola-border)" }}>
            <h1 className="text-xl font-bold font-display shrink-0">{t("notif.title", "Notifications")}</h1>
            {/* Téléphone : icônes seules (le libellé passe en title) — le texte complet reste sur PC. */}
            <div className="flex items-center gap-1 sm:gap-4">
              <button onClick={markAllRead} title={t("notif.mark_all_read", "Tout marquer comme lu")} aria-label={t("notif.mark_all_read", "Tout marquer comme lu")} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline h-9 px-2 rounded-full" style={{ color: "var(--yalola-primary)" }} data-testid="notif-page-mark-all-read">
                <CheckCheck size={18} /> <span className="hidden sm:inline">{t("notif.mark_all_read", "Tout marquer comme lu")}</span>
              </button>
              {items.length > 0 && (
                <button onClick={() => setConfirmClear(true)} title={t("notif.clear_all", "Tout vider")} aria-label={t("notif.clear_all", "Tout vider")} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline text-red-600 h-9 px-2 rounded-full" data-testid="notif-page-clear-all">
                  <Trash2 size={18} /> <span className="hidden sm:inline">{t("notif.clear_all", "Tout vider")}</span>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 px-5 py-3 border-b overflow-x-auto hide-scrollbar" style={{ borderColor: "var(--yalola-border)" }}>
            {[["all", t("notif.all", "Tout")], ["unread", `${t("notif.unread", "Non lu")}${unread > 0 ? ` (${unread})` : ""}`], ["read", t("notif.read", "Lu")], ["mentions", t("notif.mentions", "Mentions")]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className="px-4 py-1.5 rounded-full text-sm font-semibold transition-colors shrink-0 inline-flex items-center gap-1"
                style={tab === key
                  ? { background: "var(--yalola-primary)", color: "#fff" }
                  : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}
                data-testid={`notif-page-tab-${key}`}
              >
                {key === "mentions" && <AtSign size={13} />}{label}
              </button>
            ))}
          </div>

          <div className="divide-y" style={{ borderColor: "var(--yalola-border)" }}>
            {items.length === 0 && (
              <div className="p-12 text-center text-sm flex flex-col items-center gap-3" style={{ color: "var(--yalola-text-muted)" }} data-testid="notif-page-empty">
                {tab === "mentions" ? <AtSign size={32} className="opacity-30" /> : <Bell size={32} className="opacity-30" />}
                {tab === "unread"
                  ? t("notif.empty_unread", "Aucune notification non lue")
                  : tab === "read"
                    ? t("notif.empty_read", "Aucune notification lue")
                    : tab === "mentions"
                      ? t("notif.empty_mentions", "Personne ne t'a encore mentionné")
                      : t("notif.empty", "Aucune notification pour le moment")}
              </div>
            )}
            {groupNotifications(items).map((row) => {
              if (row.__group && !expandedGroups.has(row.id)) {
                const anyUnread = row.items.some((x) => !x.read);
                const GIcon = iconFor(row.type);
                return (
                  <div
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleGroup(row.id)}
                    onKeyDown={(e) => { if (e.key === "Enter") toggleGroup(row.id); }}
                    className="w-full flex items-center gap-3 px-5 py-4 text-left transition-colors hover:brightness-[0.98] cursor-pointer"
                    style={{ background: anyUnread ? "var(--yalola-primary-soft)" : "transparent" }}
                    data-testid={`notif-page-group-${row.type}`}
                  >
                    <div className="h-11 w-11 shrink-0 rounded-full grid place-items-center" style={{ background: "var(--yalola-primary)" }}>
                      <GIcon size={18} className="text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm leading-snug font-semibold">{groupTitle(row.type, row.items.length, t)}</div>
                      <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>{timeAgo(row.items[0].created_at, t)}</div>
                    </div>
                    {anyUnread && <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: "var(--yalola-primary)" }} />}
                    <ChevronDown size={18} className="shrink-0" style={{ color: "var(--yalola-text-muted)" }} />
                    <button onClick={(e) => { e.stopPropagation(); deleteGroup(row); }} className="shrink-0 p-1.5 rounded-full opacity-50 hover:opacity-100 hover:bg-black/5 transition" title={t("notif.delete", "Supprimer")} data-testid={`notif-page-group-delete-${row.type}`}>
                      <Trash2 size={16} style={{ color: "var(--yalola-text-muted)" }} />
                    </button>
                  </div>
                );
              }
              return (row.__group ? row.items : [row]).map((n) => renderItem(n));
            })}
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmClear}
        title={clearConfirmText}
        description={t("notif.clear_confirm_desc", "Cette action est définitive.")}
        confirmLabel={t("notif.clear", "Vider")}
        onConfirm={clearAll}
        onCancel={() => setConfirmClear(false)}
        testId="notif-page-clear-confirm"
      />
      <ModerationStatusModal open={!!modModal} meta={modModal} onClose={() => setModModal(null)} />
    </AppShell>
  );
}
